import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import EntryGuidePrintDocument from '../../components/EntryGuides/EntryGuidePrintDocument';
import EntryGuidePrintButton from '../../components/EntryGuides/EntryGuidePrintButton';
import EntryGuideDocumentProgressModal from '../../components/EntryGuides/EntryGuideDocumentProgressModal';
import {
  ENTRY_GUIDE_PRINT_PAGE_STYLES,
  ENTRY_GUIDE_PRINT_STYLES,
} from '../../components/EntryGuides/entryGuidePrintStyles';
import { downloadEntryGuidePdf } from '../../utils/generateEntryGuidePdf';
import { loadEntryGuideDocumentData } from '../../services/entryGuideDocumentService';
import { useModuleSettings } from '../../contexts/ModuleSettingsContext';

const GuidePrintPage = () => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const { adjustmentMassiveItemThreshold } = useModuleSettings();
  const [guide, setGuide] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [error, setError] = useState('');
  const [progress, setProgress] = useState(null);

  const guideType = searchParams.get('guide_type') || 'entrada';
  const itemsCount = Number(searchParams.get('items_count') || 0);

  useEffect(() => {
    const loadGuide = async () => {
      if (!id) return;
      setLoading(true);
      setError('');
      setProgress({
        phase: 'sync',
        status: 'processing',
        progress_percent: 5,
        message: 'Cargando datos de la guía…',
      });
      try {
        const guideData = await loadEntryGuideDocumentData(id, {
          guideType,
          itemsCount,
          thresholdSettings: { adjustment_massive_item_threshold: adjustmentMassiveItemThreshold },
          onProgress: setProgress,
        });
        setGuide(guideData);
      } catch (err) {
        console.error('Error al cargar guía:', err);
        setError(err?.message || 'No se pudo cargar la guía');
      } finally {
        setLoading(false);
        setProgress(null);
      }
    };

    loadGuide();
  }, [id, guideType, itemsCount, adjustmentMassiveItemThreshold]);

  const handleDownloadPdf = async () => {
    if (!guide || pdfLoading) return;
    setPdfLoading(true);
    try {
      await downloadEntryGuidePdf(guide);
    } catch (err) {
      console.error('Error al generar PDF:', err);
      alert(err?.message || 'No se pudo generar el PDF');
    } finally {
      setPdfLoading(false);
    }
  };

  if (loading) {
    return (
      <>
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto mb-4" />
            <p className="text-gray-600">{progress?.message || 'Cargando guía…'}</p>
          </div>
        </div>
        <EntryGuideDocumentProgressModal
          isOpen
          guideCode={guide?.code || ''}
          progress={progress}
        />
      </>
    );
  }

  if (!guide) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <p className="text-red-600 text-lg">{error || 'Guía no encontrada'}</p>
      </div>
    );
  }

  return (
    <div className="eg-print-root">
      <style>{`${ENTRY_GUIDE_PRINT_PAGE_STYLES}${ENTRY_GUIDE_PRINT_STYLES}`}</style>

      <div className="print-actions no-print">
        <EntryGuidePrintButton variant="button" onClick={() => window.print()} />
        <button
          type="button"
          onClick={handleDownloadPdf}
          disabled={pdfLoading}
          className="px-5 py-2.5 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium shadow-md transition-colors disabled:opacity-60"
        >
          {pdfLoading ? 'Generando PDF…' : 'PDF'}
        </button>
      </div>

      <EntryGuidePrintDocument guide={guide} />
    </div>
  );
};

export default GuidePrintPage;
